/**
 * onestop-canvas — the One-Stop page's rail and canvas, INSIDE the designer.
 *
 * These are not a screen. They are the two pieces the shared page designer hands
 * to a page type that has parts of its own — the same seam `CSRail` / `CSCanvas`
 * occupy for a confirmation page. Everything else an author expects is the
 * designer's and comes free: the page rail, Preview, undo/redo over the document,
 * autosave, Setup, the device width toggle.
 *
 * THE PALETTE IS DRIVEN BY THE CONSTRAINT TABLE, NOT A LIST IN THIS FILE
 * `CANVAS_BLOCK_CONSTRAINTS[type].allowed_document_kinds` is what the SERVER
 * validates against at publish, so it is what the palette offers. A list here
 * would be a second answer to "what may go on this page", and the publish
 * refusing something the palette offered is the worst version of that drift.
 *
 * Intersected with what this build can RENDER: the contract also admits the 45
 * shipped canvas types into an experience document, and those publish cleanly and
 * render nothing until the shared block library reaches this host. Offering them
 * would let an author compose a page that passes publish and comes out blank.
 *
 * SINGLETONS ARE REFUSED AT INSERT
 * `search` and `identity-panel` are `cardinality: 'singleton'`. A second one
 * publishes as `singleton-duplicated`, and locked-mode means the refusal writes
 * NOTHING — so the alternative to a quiet palette row is an author finishing a
 * page and being turned away at the end. The row states the reason as visible
 * text (never a `title`, which does not exist on a touch device) and clicking it
 * selects the block that already exists.
 */
import { Component } from "react";
import type { CSSProperties, JSX, ReactNode } from "react";
import { CANVAS_BLOCK_CONSTRAINTS } from "@v0y4ge/api-contracts";
import { BlockCanvas, BLOCK_ADD_DND } from "./block-canvas";
/* Explicit module paths, not the package barrel: the preview harness's import
   scanner does not follow a directory import — it resolves `blocks/one-stop` as
   a file, and the barrel's own `./registry` then resolves one level too high. */
import { BlockList } from "../../blocks/one-stop/registry";
import { OneStopTheme } from "../../blocks/one-stop/theme";
import { ONE_STOP_EXPERIENCE_BLOCK_TYPES, type OneStopBlock } from "../../blocks/one-stop/types";

export interface OSBlock {
  uid: string;
  id: string;
  type: string;
  version: number;
  config: Record<string, unknown>;
  visibility?: "always" | "signed-in" | "signed-out";
}

const SEC: CSSProperties = { fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#8A92A0", margin: "0 0 7px" };
const MONO: CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };

/**
 * The palette, in the order an author works (operator, 2026-09-02).
 *
 * The three things a One-Stop page is FOR come first and are the only live
 * entries. Everything else the renderer can draw follows as ordinary blocks. The
 * groups at the bottom are real registry types that are not part of this pass —
 * shown so the shape of the thing is visible, disabled so nobody builds on them
 * yet.
 */
const FAMILIES: { key: string; label: string; types: string[]; note?: string; disabled?: boolean }[] = [
  {
    key: "core", label: "What this page offers",
    note: "Tick them in Configuration and they appear here. Dragging one on brings anything you ticked that is not on the page yet.",
    types: ["book-appointment", "join-queue", "register-event"],
  },
  {
    key: "content", label: "Blocks",
    types: ["notice", "button", "faq", "quote", "stats", "social", "help-contact", "spacer"],
  },
  /* The "Lists" group is withdrawn (operator, 2026-09-02: "I don't think I need
     the lists function? I'm not sure what they are for").
     It offered the plural blocks — one block scoped by category, so new services
     appear without being added. That is a real capability, but it sat next to
     three entries that already say "Book an appointment" and the difference
     between them was invisible without knowing the model. Two ways to put
     services on a page, one of which nobody asked for, is worse than one way.
     The types still exist in the registry and still render; only the palette
     entries are gone, so a page that already has one is unaffected and this can
     come back if the category case is ever asked for. */
  { key: "offer", label: "What students can do", types: ["directory"], disabled: true },
  { key: "find", label: "Finding things", types: ["search", "context-banner"], disabled: true },
];

/**
 * ui-config — what each block is called on screen, and the one line that says
 * when to reach for it.
 *
 * The registry's type names (`context-banner`, `my-visits`) are wire vocabulary.
 * Showing them raw in a palette asks an administrator to learn the contract's
 * spelling to do their job, and puts two entries next to each other — `queues`
 * and `join-queue` — whose difference is invisible without it. The id stays
 * visible on the block itself, where it earns its place: it is what a publish
 * refusal names.
 */
const LABELS: Record<string, { label: string; blurb: string }> = {
  queues: { label: "All queues", blurb: "A live list with waits — scoped by location or org node" },
  appointments: { label: "All services", blurb: "A list with next openings — scoped by category" },
  events: { label: "All events", blurb: "What's on — scoped by category or date" },
  directory: { label: "Directory", blurb: "Who does what, by department" },
  "identity-panel": { label: "Sign in / my visits", blurb: "Their ticket, next booking and holds" },
  checklist: { label: "Their checklist", blurb: "What this student still has to do" },
  "my-visits": { label: "My visits", blurb: "Everything they have booked or joined" },
  search: { label: "Search", blurb: "Ask in your own words, plus chips" },
  "context-banner": { label: "Banner", blurb: "One message, between two dates" },
  "join-queue": { label: "Join a queue", blurb: "One desk per block, with its live wait" },
  "book-appointment": { label: "Book an appointment", blurb: "One bookable service per block" },
  "register-event": { label: "Sign up for an event", blurb: "One event per block, with registration" },
  "help-contact": { label: "Help & contact", blurb: "Links, address, hours" },
  notice: { label: "Notice", blurb: "A short callout" },
  button: { label: "Button", blurb: "One link, styled as an action" },
  faq: { label: "FAQ", blurb: "Questions and answers" },
  quote: { label: "Quote", blurb: "A short testimonial" },
  stats: { label: "Figures", blurb: "Numbers you type, not measured" },
  social: { label: "Social", blurb: "Your accounts" },
  spacer: { label: "Spacer", blurb: "Breathing room" },
};

/**
 * Starting config for a newly placed block.
 *
 * WHY A BLOCK NEEDS ONE
 * These blocks render their CONTENT and nothing else — a FAQ with no items, a
 * Quote with no text and a Figures with no figures all draw zero pixels. Created
 * with `{}`, a dropped block therefore landed correctly and appeared not to
 * arrive at all: the palette ticked, the page did not change, and the only sign
 * anything had happened was a hairline where the block was (operator, 2026-09-02).
 *
 * So a new block arrives with placeholder copy. It is placeholder, not content:
 * it says what the block is for and is the first thing an author overwrites. The
 * alternative — an empty block plus a "this block is empty" hint drawn by the
 * canvas — means the preview stops being what a student sees, which is the one
 * thing this canvas is for.
 *
 * Locale-keyed, because every authored string on a One-Stop page is a locale map.
 */
export function osSeedConfig(type: string, locale: string): Record<string, unknown> {
  const L = (text: string) => ({ [locale]: text });
  switch (type) {
    case "notice":
      return { text: L("Something students should know."), tone: "info" };
    case "button":
      /* A target is required, and "/" is the only honest placeholder: it is a
         valid in-platform path, so the block renders and publishes, and it points
         at the portal's own front page rather than at a URL nobody chose. */
      return { label: L("Take me there"), target: { kind: "deep-link", route: "/", params: {} }, variant: "primary" };
    case "faq":
      return { items: [{ question: L("A question students ask"), answer: L("The answer.") }] };
    case "quote":
      return { text: L("What someone said about this service."), attribution: L("Their name") };
    case "stats":
      return { items: [{ value: "100", label: L("What this number counts") }] };
    case "social":
      return { networks: [] };
    case "help-contact":
      return { linkGroups: [{ title: L("Get help"), links: [] }] };
    case "spacer":
      return { size: "medium" };
    default:
      return {};
  }
}

export function osLabel(type: string): string {
  return LABELS[type]?.label ?? type;
}

const RENDERABLE: ReadonlySet<string> = new Set(ONE_STOP_EXPERIENCE_BLOCK_TYPES);


function allowedInExperience(type: string): boolean {
  const kinds = CANVAS_BLOCK_CONSTRAINTS[type as keyof typeof CANVAS_BLOCK_CONSTRAINTS]?.allowed_document_kinds;
  return Array.isArray(kinds) ? kinds.includes("experience") : false;
}

export interface OSRailProps {
  blocks: OSBlock[];
}

/**
 * The palette — the designer's own, in the designer's own shape.
 *
 * A two-column grid of small labelled buttons with a ✓ on the ones already on
 * the page. DRAG ONLY (operator, 2026-09-02): a click does nothing at all. The
 * ✓ is an indicator, not a toggle — it was previously the same button meaning
 * "add" and "remove" depending on state, which is how a page ends up missing a
 * block nobody meant to delete.
 *
 * There is no stacked list of placed blocks here either. The CANVAS is that
 * list — it is the page, in order, blocks drag to reorder, and each carries its
 * own ×. A second ordered list in the rail was a third place to look at the
 * same eight things.
 */
export function OSRail(props: OSRailProps): JSX.Element {
  return (
    <>
      {FAMILIES.map((family) => {
        const types = family.types.filter((t) => RENDERABLE.has(t) && allowedInExperience(t));
        if (types.length === 0) return null;
        const off = family.disabled === true;
        return (
          <div key={family.key}>
            <div style={{ ...SEC, color: off ? "#C7CBD6" : "#8A92A0" }}>
              {family.label}
              {off ? <span style={{ marginLeft: 6, fontWeight: 700, letterSpacing: 0, textTransform: "none", fontSize: 9.5 }}>· not yet</span> : null}
            </div>
            {family.note ? (
              <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.45, margin: "-3px 0 7px" }}>{family.note}</div>
            ) : null}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 18 }}>
              {types.map((type) => {
                const used = props.blocks.some((b) => b.type === type);
                const meta = LABELS[type];
                return (
                  <button
                    key={type}
                    /* Drag ONLY (operator, 2026-09-02) — matching the shared
                       palette. A click did two opposite things depending on
                       whether the block was already placed; removal is now the ×
                       on the block, and the ✓ is purely an indicator. */
                    draggable={!off}
                    onDragStart={(e) => {
                      if (off) { e.preventDefault(); return; }
                      e.dataTransfer?.setData(BLOCK_ADD_DND, type);
                      if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy";
                    }}
                    title={off ? `${meta?.label ?? type} — not in this version of the One-Stop designer yet`
                      : used ? `${meta?.label ?? type} is on this page — remove it with the × on the block`
                      : meta?.blurb ?? "Drag onto the page to place it"}
                    style={{
                      padding: "11px 6px",
                      border: `1px solid ${off ? "#EEF0F3" : used ? "#5142C7" : "#E6E8EC"}`,
                      background: off ? "#FBFBFC" : used ? "#F6F2FE" : "#FFFFFF",
                      color: off ? "#C7CBD6" : used ? "#5142C7" : "#333333",
                      borderRadius: 6, fontSize: 11, fontWeight: 600,
                      cursor: off ? "not-allowed" : "grab",
                      position: "relative", fontFamily: "inherit", textAlign: "center", lineHeight: 1.3,
                    }}
                  >
                    {meta?.label ?? type}
                    {used && !off ? (
                      <span style={{ position: "absolute", top: 3, right: 3, width: 12, height: 12, borderRadius: 999, background: "#5142C7", color: "#FFFFFF", fontSize: 8, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>✓</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}

/**
 * One block, one boundary.
 *
 * The canvas renders the STUDENT's components over a draft, and a draft can hold
 * shapes a portal-resolved page never would — an event with no date, a config
 * field half-typed. When one of those throws, React unmounts the whole tree, so
 * a row that cannot draw takes the entire designer with it and the author loses
 * the screen they were working on.
 *
 * Per BLOCK rather than per canvas, deliberately: the rest of the page keeps
 * rendering, and the failure is located on the block that caused it instead of
 * replacing everything with one apology.
 *
 * `blockKey` resets it — without that, fixing the config in the inspector would
 * leave the boundary latched in its error state and look like the fix did
 * nothing.
 */
class BlockBoundary extends Component<{ label: string; blockKey: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidUpdate(prev: { blockKey: string }) {
    if (prev.blockKey !== this.props.blockKey && this.state.failed) this.setState({ failed: false });
  }
  componentDidCatch(err: unknown) {
    // eslint-disable-next-line no-console -- the block is named in the message
    console.error(`[one-stop] "${this.props.label}" could not be previewed.`, err);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div style={{ border: "1px dashed #F3D0CB", background: "#FFF8F7", borderRadius: 10, padding: "14px 12px", fontSize: 11.5, color: "#C0392B", lineHeight: 1.55 }}>
        <b>{this.props.label}</b> can’t be previewed with its current settings. The block is still on
        the page and still saves — open it in the editor to see what it needs.
      </div>
    );
  }
}

export interface OSCanvasProps {
  blocks: OSBlock[];
  /** The same blocks, with preview `data` fragments resolved against the real
   *  records the designer already holds. Rendered instead of `blocks`, so a
   *  scoped block shows the desks and services an author actually picked. */
  rendered?: { id: string; type: string; version: number; config: Record<string, unknown>; visibility?: string; data?: unknown; notice?: string }[];
  locale: string;
  timeZone: string;
  subtitle?: string;
  sel: string | null;
  onSelect: (id: string | null) => void;
  onChange: (next: OSBlock[]) => void;
  /** Mints a unique uid and a unique block id for a newly dropped type. Owned
   *  by the screen because block ids must be unique across the PAGE, which this
   *  component only sees a slice of. */
  onInsert: (type: string, at: number) => void;
  /** Preview mode: the page as a student sees it. No insertion lines, no drag,
   *  no per-block controls, no selection ring — the same component so the two
   *  surfaces cannot render the page differently, with authoring switched off so
   *  a preview cannot be mistaken for an editor. */
  readOnly?: boolean;
}

/**
 * The page as a student sees it, rendered by the student's own components.
 *
 * `BlockList` from `blocks/one-stop`, `ctx.host = "builder"` — not a second set
 * of components, so what the admin sees is what the student gets. Each block is
 * wrapped in a selection shell so clicking the CANVAS selects, which is what the
 * rest of the designer does and what an author reaches for first.
 *
 * The wrapper is a plain `div` with no layout of its own precisely because the
 * renderer composes a hero band out of the leading blocks: anything that changed
 * the box model here would change the thing this preview exists to show.
 */
export function OSCanvas(props: OSCanvasProps): JSX.Element {
  /* A thin wrapper over BlockCanvas (2026-09-02). Everything about the GESTURE —
     drop geometry, the indicator, selection, drag-to-reorder, the per-block
     controls — is shared with every other journey type now, so One-Stop cannot
     drift into feeling different again. What stays here is the only thing that is
     genuinely One-Stop's: each block is drawn by the STUDENT's renderer, inside
     its own error boundary. */
  return (
    <div style={{ marginTop: 6 }}>
      <OneStopTheme />
      <BlockCanvas
        blocks={props.blocks}
        sel={props.sel}
        readOnly={props.readOnly}
        inset={0}
        labelOf={(b) => osLabel(b.type)}
        onSelect={props.onSelect}
        onMove={(from, to) => {
          const next = props.blocks.slice();
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved!);
          props.onChange(next);
        }}
        onInsert={props.onInsert}
        onRemove={(uid) => {
          const gone = props.blocks.find((b) => b.uid === uid);
          props.onChange(props.blocks.filter((b) => b.uid !== uid));
          if (gone && props.sel === gone.id) props.onSelect(null);
        }}
        emptyState={
          <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "22px 14px", textAlign: "center", fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>
            Nothing on this page yet.
            {props.readOnly ? null : " Drag a block from the left to place it — each service, queue and event is its own block."}
          </div>
        }
        renderBlock={(block, i) => (
          <BlockBoundary label={osLabel(block.type)} blockKey={JSON.stringify(block.config ?? {})}>
            <BlockList
              /* The authoring block and the portal block differ by one field: the
                 resolved `data` a draft never has. The renderers narrow on `data`
                 by `kind` and never assume it, so the cast crosses two schemas'
                 declarations rather than two shapes. */
              blocks={[(props.rendered?.[i] ?? block)] as unknown as OneStopBlock[]}
              heroEyebrow={undefined}
              ctx={{
                locale: props.locale,
                timeZone: props.timeZone,
                /* Inert: the card renders, disabled, so an author sees the real
                   component and cannot fire a live query from a preview. */
                host: "builder",
                search: null,
              }}
            />
            {/* Why this block shows fewer rows than were picked. Under the block
                rather than inside it: the block is the student's component and
                this sentence is for the author. */}
            {props.rendered?.[i]?.notice ? (
              <div style={{ display: "flex", gap: 7, alignItems: "flex-start", margin: "4px 0 2px", padding: "7px 10px", borderRadius: 7, background: "#FFFBF2", border: "1px solid #F0DCB8", fontSize: 10.5, lineHeight: 1.5, color: "#8A5A08" }}>
                <span style={{ flex: "none", fontWeight: 800 }}>!</span>
                <span>{props.rendered[i]!.notice}</span>
              </div>
            ) : null}
          </BlockBoundary>
        )}
      />
      {props.readOnly ? null : (
        <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", marginTop: 8, ...MONO }}>
          the student's renderer · lists are empty until a student loads the page
        </div>
      )}
    </div>
  );
}
